import { useEffect, useState } from 'react';
import { Activity } from 'lucide-react';
import clsx from 'clsx';
import { useMatch } from 'react-router-dom';
import { useTransport, formatBytesPerSec } from '@/store/transport';

/**
 * Topbar throughput meter — shows the current video/control stream
 * bandwidth for the device matched in the URL. Reads from the
 * dual-transport WS (`/api/devices/transport/events`), which emits a
 * `throughput-sample` frame per device per second based on bytes
 * pushed through the screen-stream WS.
 *
 * UX notes:
 *   - Only visible on a device-scoped route (`/devices/:serial/*`).
 *     On the dashboard we have no single "current device" so the
 *     meter would be ambiguous.
 *   - If no recent sample exists (no mirror open) the meter shows
 *     `空闲`. After 3 s of silence the displayed number fades to
 *     `0 B/s` to make it obvious that the meter is live.
 *   - The meter intentionally measures the WS-bridge sendBin path
 *     only — NOT every adb byte the host shuffles. That's accurate
 *     for the user's mental model of "什么连接方式 + 多快" because
 *     the screen stream is by far the loudest channel.
 */
export function TopbarThroughputMeter(): JSX.Element | null {
  const match = useMatch('/devices/:serial/*');
  const serial = match?.params.serial;
  const sample = useTransport((s) => (serial ? s.throughputBySerial[serial] : undefined));
  // Tick once per second so the "idle decay" branch re-evaluates even
  // when no new sample arrived. Cheap because the topbar is one mount.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => (t + 1) % 1024), 1000);
    return () => window.clearInterval(id);
  }, []);
  if (!serial) return null;
  const now = Date.now();
  const ageMs = sample ? now - sample.ts : Infinity;
  const bps =
    sample && ageMs <= 3_000 ? sample.bytesPerSec : sample ? 0 : null;
  const label =
    bps === null
      ? '空闲'
      : bps <= 0
        ? '0 B/s'
        : formatBytesPerSec(bps);
  const active = bps !== null && bps > 0;
  return (
    <div
      className={clsx(
        'inline-flex h-8 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium tabular-nums',
        active
          ? 'border-indigo-500/50 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300'
          : 'border-line bg-bg-soft text-ink-muted'
      )}
      title={
        bps === null
          ? '当前未在镜像 / 流传输；连接保持在线但无数据流'
          : `近 1 秒视频流速率 ${formatBytesPerSec(bps)}（包含 scrcpy 协议帧）`
      }
    >
      <Activity className={clsx('h-3.5 w-3.5', active && 'animate-pulse')} />
      <span>{label}</span>
    </div>
  );
}
